import './RecReviewCharts.scss';

import { IconButton, type SelectChangeEvent } from '@mui/material';
import {
	setRecReviewChartDayTypes,
	setRecReviewChartDays,
} from 'features/my-view/redux/my-view-slice';
import {
	selectRecReviewChartDayTypes,
	selectRecReviewChartDays,
} from 'features/my-view/redux/selectors';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
	daysOptions,
	graphOptions,
	viewModeOptions,
	weekOptions,
} from './config';

import { DropdownMenu } from 'components/DropdownMenu/DropdownMenu';
import { usePropertyContext } from 'context/propertyContext';
import { ChartContent } from 'features/rules/rates/components/chart-content';
import { useRecReviewCharts } from 'hooks/useRecReviewCharts';
import { labels } from 'locales/en.label';
import { useState } from 'react';
import { FiChevronLeft } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import type { RecReviewChartArgs } from '../../../types/RecReviewTypes';
import { DemandChart } from './DemandChart';
import { InventoryChart } from './InventoryChart';

export const RecReviewCharts = () => {
	const dispatch = useAppDispatch();
	const days = useAppSelector(selectRecReviewChartDays);
	const dayType = useAppSelector(selectRecReviewChartDayTypes) || 'all';
	const [chartView, setChartType] = useState('demand');
	const [rateViewMode, setRateViewMode] = useState('daily');
	const { property } = usePropertyContext();
	const propertyId = property?.propertyId || '';
	const handleDayChange = (e: SelectChangeEvent) => {
		dispatch(setRecReviewChartDays(e.target.value));
	};
	const handleWeekChange = async (e: SelectChangeEvent) => {
		dispatch(setRecReviewChartDayTypes(e.target.value));
	};
	const handleTestChange = (e: any) => {
		setChartType(e.target.value);
	};
	const handleViewModeChange = (e: SelectChangeEvent) => {
		setRateViewMode(e.target.value);
	};
	const history = useHistory();
	const handleClick = () => history.push('/my-view/metrics');

	const { chartData, loading } = useRecReviewCharts(propertyId, dayType, days);

	return (
		<div className='chart__container'>
			<div className='chart__backBtn'>
				<IconButton
					disableFocusRipple
					disableRipple
					aria-label='return'
					onClick={handleClick}
				>
					<FiChevronLeft size={16} className='chart__backBtn-icon' />
					<span className='chart__backBtn-text'>
						{labels.rec_review.chart.return_to_grid}
					</span>
				</IconButton>
			</div>
			<div className='chart__header'>
				<div className='chart__dropdown-graphType'>
					<DropdownMenu
						options={graphOptions}
						preSelectValue={chartView}
						handleChange={handleTestChange}
					/>
				</div>
				{chartView === 'daily-rate-recs' && (
					<div className='chart__dropdown-viewMode'>
						<DropdownMenu
							options={viewModeOptions}
							preSelectValue={rateViewMode}
							handleChange={handleViewModeChange}
						/>
					</div>
				)}
				<div
					className='chart__dropdown-days'
					style={{
						visibility: chartView === 'daily-rate-recs' ? 'hidden' : 'visible',
					}}
				>
					<DropdownMenu
						options={daysOptions}
						preSelectValue={days || ''}
						handleChange={handleDayChange}
					/>
				</div>
				<div
					className='chart__dropdown-weeks'
					style={{
						visibility: chartView === 'daily-rate-recs' ? 'hidden' : 'visible',
					}}
				>
					<DropdownMenu
						options={weekOptions}
						preSelectValue={dayType || ''}
						handleChange={handleWeekChange}
					/>
				</div>
			</div>
			<div className='chart__content'>
				{chartView === 'demand'
					? chartData && (
							<DemandChart
								dayType={dayType as RecReviewChartArgs['dayType']}
								chartData={chartData}
								loading={loading}
								days={Number.parseInt(days)}
							/>
						)
					: chartView === 'inventory'
						? chartData && (
								<InventoryChart
									dayType={dayType as RecReviewChartArgs['dayType']}
									chartData={chartData}
									loading={loading}
									days={Number.parseInt(days)}
								/>
							)
						: chartView === 'daily-rate-recs' && (
								<ChartContent
									viewMode={rateViewMode as 'daily' | 'average'}
									className='tw-w-full tw-max-h-[400px]'
									height={420}
								/>
							)}
			</div>
		</div>
	);
};
